import React, { useReducer, useState } from 'react'
import getConfig from 'next/config'
import getFeatureFlags from '@src/utils/getFeatureFlags'
import { FormattedMessage, useIntl } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import { ButtonBase, Divider, Drawer } from '@mui/material'
import { ArrowBack, Close, KeyboardArrowRight } from '@mui/icons-material'
import addLevelKeyToNav, { Group } from '@utils/addLevelKeyToNav'
/*
  The account popout on mobile gets it's menu items and message definitions from ./data.
  Those messages are then dynamically rendered and always need to be available even knowing eventually they'll be inlined elsewhere.
  As will other messages directly called in this file. As such the account flyout messages now have their own messages file.
*/
import accountMenuMessages from '@utils/MessageSets/defineAccountMenuMessages'
import { MenuOption } from './HeaderUtils'

import {
  sendHeaderAccountMenuEvents,
  sendHeaderTopMenuEvents,
} from '@utils/analytics'
import { useUserSession } from '@utils/useUserSession'
import { useCurrentUser } from '@utils/useCurrentUser'
import { Link } from '@src/components/Link'
import {
  homeRoute,
  loginRoute,
  orderLookupRoute,
  quickOrderRoute,
  registerRoute,
  useRouter,
} from '@src/routes'
import { AemTopNav, NotificationsResponse } from '@src/types/graphql-types'
import { useAemHeaderFooterQuery } from '@src/queries/AemQuery.generated'
import MobileRegionAndCountrySelectModal from './MobileRegionAndCountrySelectModal'
import { MEGA_NAV_ITEMS, MegaNavSections, NavItem } from './data' // TODO: Clean this up
import CompanyLogo from '@src/components/CompanyLogo'
import HandleMarkup from '@src/components/HandleMarkup'
import MobileLanguageSelectModal from './MobileLanguageSelectModal'
import { useCountriesData } from '@src/utils/useCountriesData'
import { Linq } from '@src/components/Linq'
import { useCompanyName } from '@utils/useCompanyName'
import NotificationCenter from './NotificationsCenter'
import { parseRegionalUrl } from '@src/utils/regional'
import NewTag from '../NewTag'

const {
  publicRuntimeConfig: { supportedLanguageLabels },
} = getConfig()

const featureFlags = getFeatureFlags()

const useStyles = makeStyles()((theme) => ({
  paper: {
    right: 0,
  },
  background: {
    position: 'relative',
    height: '100%',
    overflow: 'hidden',
  },
  container: {
    height: `calc(100% - ${theme.spacing(20)})`,
    overflowY: 'auto',
  },
  content: {
    color: theme.palette.common.black,
    padding: theme.spacing(0, 5, 5),
  },
  header: {
    display: 'flex',
    width: '100%',
    padding: theme.spacing(4),
    marginBottom: theme.spacing(6),
    alignItems: 'center',
    justifyContent: 'flex-end',
    borderBottom: `1px solid ${theme.palette.brand.main}`,
    '& > * + *': {
      marginLeft: 'auto',
    },
  },
  levelTitle: {
    color: theme.palette.brand.main,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightBold,
  },
  backIcon: {
    color: theme.palette.brand.main,
    fontSize: theme.typography.pxToRem(20),
  },
  closeIcon: {
    color: theme.palette.brand.main,
    fontSize: theme.typography.pxToRem(24),
  },
  navItems: {
    marginBottom: theme.spacing(7),
    paddingBottom: theme.spacing(3),
  },
  navItem: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: theme.typography.pxToRem(18),
    marginBottom: theme.spacing(4),
    color: theme.palette.common.black,
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'left',
  },
  subNavItem: {
    fontSize: theme.typography.pxToRem(14),
  },
  navItemArrow: {
    fontSize: theme.typography.pxToRem(22),
    color: theme.palette.brand.main,
  },
  navItemLinkArrow: {
    color: theme.palette.common.black,
  },
  actionItem: {
    display: 'flex',
    alignItems: 'center',
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.common.black,
  },
  actionItemTextButton: {
    fontSize: theme.typography.pxToRem(14),
  },
  listValue: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.grey[800],
  },
  buttonGroup: { minWidth: 54 },
  actionFooter: {
    position: 'fixed',
    bottom: theme.spacing(4),
    left: theme.spacing(5),
  },
  suffix: {
    marginLeft: theme.spacing(4),
  },
  vr1: {
    marginBottom: theme.spacing(1),
  },
  vr4: {
    marginBottom: theme.spacing(4),
  },
  vr6: {
    marginBottom: theme.spacing(6),
  },
  companyLogo: {
    height: '100%',
    '& > svg': {
      fontSize: '90px',
    },
  },
  actions: {
    gap: '30px',
    display: 'flex',
  },
}))

interface Props {
  isOpen: boolean
  onClose: () => void
  notificationsData: NotificationsResponse[]
}

interface AEMNavChildObj {
  menuItem: boolean
  openNewTab: boolean
  title: string
  items: AEMNavChildObj[]
  label?: string
  eventLabel?: string
  url?: string
  childrens?: AEMNavChildObj[]
  level?: string
}

interface AEMNavDataParentItem {
  label: string
  items: AEMNavChildObj[]
  level?: string
  eventLabel?: string
}

interface AEMNavData {
  [toplevelkey: string]: AEMNavDataParentItem
}

interface AEMMEgaNavState {
  activeNavItem: string | null
  activeSubNavItem: AEMNavChildObj | null
  activeSubNavItemChild: AEMNavChildObj | null
}

enum AemMegaNavActionTypes {
  SET_ACTIVE_NAV_ITEM = 'setActiveNavItem',
  SET_ACTIVE_SUBNAV_ITEM = 'setActiveSubNavItem',
  SET_ACTIVE_SUBNAV_CHILD = 'setActiveSubnavChild',
  CLOSE = 'close,',
}

interface SetActiveNavItemAction {
  type: AemMegaNavActionTypes.SET_ACTIVE_NAV_ITEM
  payload: string | null
}

interface SetActiveSubNavItemAction {
  type: AemMegaNavActionTypes.SET_ACTIVE_SUBNAV_ITEM
  payload: AEMNavChildObj | null
}

interface SetActiveSubNavChildAction {
  type: AemMegaNavActionTypes.SET_ACTIVE_SUBNAV_CHILD
  payload: AEMNavChildObj | null
}

interface CloseAction {
  type: AemMegaNavActionTypes.CLOSE
  payload?: void
}

type AEMMegaNavActions =
  | SetActiveNavItemAction
  | SetActiveSubNavItemAction
  | SetActiveSubNavChildAction
  | CloseAction

const initialState: AEMMEgaNavState = {
  activeNavItem: null,
  activeSubNavItem: null,
  activeSubNavItemChild: null,
}

const reducer = (state: AEMMEgaNavState, action: AEMMegaNavActions) => {
  switch (action.type) {
    case AemMegaNavActionTypes.SET_ACTIVE_NAV_ITEM:
      return {
        ...state,
        activeNavItem: action.payload,
      }
    case AemMegaNavActionTypes.SET_ACTIVE_SUBNAV_ITEM:
      return { ...state, activeSubNavItem: action.payload }
    case AemMegaNavActionTypes.SET_ACTIVE_SUBNAV_CHILD:
      return { ...state, activeSubNavItemChild: action.payload }
    case AemMegaNavActionTypes.CLOSE:
      return initialState
    default:
      return state
  }
}

const AEMNavDataMapper = (rawData: AemTopNav[] = []): AEMNavData | null => {
  const AEMSubNavMapper = (childArray?: AEMNavChildObj[]): AEMNavChildObj[] => {
    if (!childArray) {
      return []
    }

    return childArray.map((child) => {
      // Null check saftey to ensure we don't try to read values from an undefined prop
      const subChildren = child.childrens ? child.childrens : []
      return {
        ...child,
        label: child.title,
        items: AEMSubNavMapper(subChildren),
      }
    })
  }

  const mappedData: AEMNavData = {}

  rawData.forEach((topNavItem) => {
    const label = topNavItem?.title || ''
    const items = (topNavItem?.items as any[]) || ([] as NavItem[])
    mappedData[label] = {
      label,
      items: items.map((item) => {
        return {
          ...item,
          label: item?.title,
          items: AEMSubNavMapper(item.childrens),
        }
      }),
    }
  })

  return mappedData
}

const MobileMegaNav: React.FC<Props> = ({
  isOpen,
  onClose,
  notificationsData,
}) => {
  const { classes, cx } = useStyles()
  const intl = useIntl()
  const router = useRouter()
  const { userIsLoggedIn, isOrderStatusCustomer, isQuoteEnabled } =
    useCurrentUser()
  const { userSession, userSessionActions } = useUserSession()
  const { getCountryProp } = useCountriesData()

  const stratosMultiRegion = featureFlags.stratosMultiRegion

  const userCountry = (userSession.country || '').toLocaleUpperCase()
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false)
  const [isRegionAndCountryModalOpen, setIsRegionAndCountryModalOpen] =
    useState(false)
  const [{ activeNavItem, activeSubNavItemChild, activeSubNavItem }, dispatch] =
    useReducer(reducer, initialState)
  const { data } = useAemHeaderFooterQuery()
  const AEMDataTopNav =
    data && addLevelKeyToNav(data?.aemHeaderFooter?.header?.topnav as Group[])

  const AEMTopNavMapped = AEMNavDataMapper(AEMDataTopNav as AemTopNav[])

  const languages: string[] = getCountryProp(
    userSession.country?.toUpperCase(),
    'supportedLanguages'
  )
  const companyName = useCompanyName()
  const { formatMessage } = useIntl()

  const handleClose = () => {
    onClose()
    dispatch({ type: AemMegaNavActionTypes.CLOSE })
  }

  const handleBack = () => {
    if (activeSubNavItemChild) {
      return dispatch({
        type: AemMegaNavActionTypes.SET_ACTIVE_SUBNAV_CHILD,
        payload: null,
      })
    }
    if (activeSubNavItem) {
      return dispatch({
        type: AemMegaNavActionTypes.SET_ACTIVE_SUBNAV_ITEM,
        payload: null,
      })
    }
    return dispatch({
      type: AemMegaNavActionTypes.SET_ACTIVE_NAV_ITEM,
      payload: null,
    })
  }

  const handleLanguageSelect = (lang: string): void => {
    sendHeaderTopMenuEvents()
    handleClose()
    userSessionActions.setLanguage(lang)
    window.location.reload()
  }

  const navItemClassNames = cx(classes.navItem, {
    [classes.subNavItem]: !!activeNavItem || !!activeSubNavItem,
  })

  const renderNavItem = (
    item: AEMNavDataParentItem | AEMNavChildObj | NavItem
  ) => {
    const safeLabel = item.label || ''
    let safeUrl = (item as AEMNavChildObj).url
    const isLearningCenter = safeUrl?.toLowerCase().includes('learning-center')
    const safeType = (item as NavItem).type

    // AEM3-1895 - Some countries(e.g AD,GL) that come under INTL in cms-commons do not have pages specifically created for those countries.
    // Hence BE sends a redirect url for the navigation pages of those countries.Because the country/language of the redirect url would be
    // different from the user country, they need to be updated so that the page redirects correctly.
    if (safeUrl) {
      const {
        basename: { country, language },
      } = parseRegionalUrl(router.asPath)
      const {
        basename: { country: navCountry },
        pathAfterBasename,
      } = parseRegionalUrl(safeUrl)
      if (navCountry && country !== navCountry && pathAfterBasename)
        safeUrl = `/${country}/${language}${pathAfterBasename}`
    }
    if (safeType === 'divider') {
      return (
        <Divider
          key={safeLabel}
          variant="fullWidth"
          className={navItemClassNames}
        />
      )
    }

    if (safeUrl && !item?.items?.length) {
      return (
        <Link key={safeLabel} href={safeUrl}>
          <a
            key={safeLabel}
            className={navItemClassNames + ` aem-navlinks-level-${item?.level}`}
            onClick={() => {
              dispatch({ type: AemMegaNavActionTypes.CLOSE })
              onClose()
            }}
            role="button"
            tabIndex={0}
            onKeyUp={(event) => {
              if (event.key === 'Enter') {
                dispatch({ type: AemMegaNavActionTypes.CLOSE })
              }
            }}
          >
            <span>
              <HandleMarkup value={safeLabel} />
              {'suffix' in item && item.suffix && (
                <span className={classes.suffix}>{item.suffix}</span>
              )}
              {isLearningCenter && <NewTag />}
            </span>
          </a>
        </Link>
      )
    }

    return (
      <ButtonBase
        key={safeLabel}
        className={
          navItemClassNames +
          (item?.level !== undefined
            ? ` aem-navlinks-level-${item?.level}`
            : '')
        }
        onClick={() => {
          if (activeNavItem && activeSubNavItem) {
            dispatch({
              type: AemMegaNavActionTypes.SET_ACTIVE_SUBNAV_CHILD,
              payload: item as AEMNavChildObj,
            })
          } else if (activeNavItem) {
            dispatch({
              type: AemMegaNavActionTypes.SET_ACTIVE_SUBNAV_ITEM,
              payload: item as AEMNavChildObj,
            })
          } else {
            dispatch({
              type: AemMegaNavActionTypes.SET_ACTIVE_NAV_ITEM,
              payload: safeLabel || null,
            })
          }
        }}
      >
        <HandleMarkup value={safeLabel} />
        <KeyboardArrowRight className={classes.navItemArrow} />
      </ButtonBase>
    )
  }

  const renderNavItems = () => {
    const navData = AEMTopNavMapped || {}
    if (activeNavItem && activeSubNavItem && activeSubNavItemChild) {
      const subNavChild = activeSubNavItemChild

      return subNavChild.items?.map((item) => renderNavItem(item))
    }

    if (activeNavItem && activeSubNavItem) {
      const subNav = activeSubNavItem

      return subNav.items?.map((item) => renderNavItem(item))
    }

    if (activeNavItem && activeNavItem !== MegaNavSections.Account) {
      const nav: AEMNavDataParentItem = navData[activeNavItem]
      return nav.items?.map((item: AEMNavChildObj | NavItem) =>
        renderNavItem(item)
      )
    }

    if (activeNavItem && activeNavItem === MegaNavSections.Account) {
      const nav: NavItem = MEGA_NAV_ITEMS(
        isOrderStatusCustomer,
        isQuoteEnabled
      )[activeNavItem]
      return nav.items?.map((item) => {
        return renderNavItem({
          ...item,
          label: accountMenuMessages[item.label]
            ? intl.formatMessage(accountMenuMessages[item.label])
            : item.label,
          eventLabel: accountMenuMessages[item.label]?.defaultMessage,
        })
      })
    }

    return Object.keys(navData)
      .filter((item) => {
        if (item === MegaNavSections.Account) {
          return userIsLoggedIn
        }
        return true
      })
      .map((item) => renderNavItem(navData[item]))
  }

  let levelTitle = ''
  if (activeSubNavItemChild) {
    levelTitle = activeSubNavItemChild.title
  } else if (activeSubNavItem) {
    levelTitle = activeSubNavItem.title
  } else if (activeNavItem) {
    levelTitle =
      activeNavItem === MegaNavSections.Account
        ? intl.formatMessage(accountMenuMessages[MegaNavSections.Account])
        : activeNavItem
  }

  return languages ? (
    <Drawer
      classes={{ paperAnchorLeft: classes.paper }}
      open={isOpen}
      onClose={handleClose}
    >
      <div className={classes.background}>
        <div className={classes.header}>
          {activeNavItem || activeSubNavItem ? (
            <ButtonBase
              onClick={handleBack}
              aria-label={formatMessage({
                id: 'GO_BACK_ONE_LEVEL',
                defaultMessage: 'Go Back One Level',
              })}
            >
              <ArrowBack className={classes.backIcon} />
            </ButtonBase>
          ) : (
            <Linq
              href={homeRoute.index()}
              className={classes.companyLogo}
              aria-label={`${companyName} ${formatMessage({
                id: 'HOME',
                defaultMessage: 'Home',
              })}`}
            >
              <CompanyLogo fontSize="inherit" />
            </Linq>
          )}
          {levelTitle !== '' && (
            <h2 className={classes.levelTitle}>{levelTitle}</h2>
          )}
          <div className={classes.actions}>
            {userIsLoggedIn && <NotificationCenter data={notificationsData} />}
            <ButtonBase
              onClick={handleClose}
              aria-label={formatMessage({
                id: 'CLOSE_MAIN_MENU',
                defaultMessage: 'Close Main Menu',
              })}
            >
              <Close className={classes.closeIcon} />
            </ButtonBase>
          </div>
        </div>
        <div className={classes.container}>
          <div className={classes.content}>
            <div className={classes.navItems}>{renderNavItems()}</div>
            {!activeNavItem && (
              <div>
                <div className={classes.vr4}>
                  <Link href={quickOrderRoute.index()}>
                    <a
                      className={classes.actionItem}
                      data-testid="header-mobile-quick-order"
                      id="header-mobile-quick-order"
                    >
                      <FormattedMessage
                        id="QUICK_ORDER"
                        defaultMessage="Quick Order"
                      />
                    </a>
                  </Link>
                </div>
                <div className={classes.vr4}>
                  <Link href={orderLookupRoute.index()}>
                    <a className={classes.actionItem}>
                      <FormattedMessage
                        id="ORDER_LOOKUP"
                        defaultMessage="Order Lookup"
                      />
                    </a>
                  </Link>
                </div>
                {userIsLoggedIn ? (
                  <>
                    <div className={classes.vr4}>
                      <ButtonBase
                        className={cx(classes.navItem, classes.subNavItem)}
                        onClick={() => {
                          dispatch({
                            type: AemMegaNavActionTypes.SET_ACTIVE_NAV_ITEM,
                            payload: MegaNavSections.Account,
                          })
                        }}
                      >
                        <div>
                          <FormattedMessage
                            id="PRIMARY_NAV_ACCOUNT"
                            defaultMessage="Account"
                          />
                        </div>
                        <KeyboardArrowRight className={classes.navItemArrow} />
                      </ButtonBase>
                    </div>
                    <div>
                      <a
                        href="/logout"
                        className={classes.actionItem}
                        onClick={async (e) => {
                          e.preventDefault()
                          sendHeaderAccountMenuEvents(MenuOption.Logout)
                          handleClose()
                          await userSessionActions.logout(router)
                        }}
                        data-testid="header-logout"
                        id="header-logout"
                      >
                        <FormattedMessage
                          id="SIGN_OUT"
                          defaultMessage="Sign Out"
                        />
                      </a>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={classes.vr4}>
                      <Link href={loginRoute.index()}>
                        <a
                          className={classes.actionItem}
                          data-testid="header-mobile-login"
                          id="header-mobile-login"
                        >
                          <FormattedMessage
                            id="SIGN_IN"
                            defaultMessage="Sign In"
                          />
                        </a>
                      </Link>
                    </div>
                    <div>
                      <Link href={registerRoute.index()}>
                        <a
                          className={classes.actionItem}
                          data-testid="header-mobile-signin"
                          id="header-mobile-signin"
                        >
                          <FormattedMessage
                            id="SIGN_UP"
                            defaultMessage="Sign Up"
                          />
                        </a>
                      </Link>
                    </div>
                  </>
                )}
                <div className={classes.actionFooter}>
                  <div className={classes.vr1}>
                    {stratosMultiRegion && !userIsLoggedIn ? (
                      <ButtonBase
                        onClick={() =>
                          setIsRegionAndCountryModalOpen(
                            !isRegionAndCountryModalOpen
                          )
                        }
                        className={classes.actionItemTextButton}
                      >
                        <FormattedMessage
                          id="COUNTRY"
                          defaultMessage="Location"
                        />
                      </ButtonBase>
                    ) : (
                      <div className={classes.actionItemTextButton}>
                        <FormattedMessage
                          id="COUNTRY"
                          defaultMessage="Location"
                        />
                      </div>
                    )}
                  </div>
                  <div className={cx(classes.listValue, classes.vr4)}>
                    {userCountry}
                  </div>
                  <MobileRegionAndCountrySelectModal
                    isOpen={isRegionAndCountryModalOpen}
                    onClose={setIsRegionAndCountryModalOpen}
                  />

                  <div className={classes.vr1}>
                    <ButtonBase
                      onClick={() =>
                        setIsLanguageModalOpen(!isLanguageModalOpen)
                      }
                      className={classes.actionItemTextButton}
                    >
                      <FormattedMessage
                        id="LANGUAGE"
                        defaultMessage="Language"
                      />
                    </ButtonBase>
                  </div>
                  <div className={classes.listValue}>
                    {
                      supportedLanguageLabels[
                        userSession.language.toUpperCase()
                      ]
                    }
                  </div>
                  <MobileLanguageSelectModal
                    languages={languages}
                    currentLanguage={userSession.language}
                    onLanguageSelect={handleLanguageSelect}
                    isOpen={isLanguageModalOpen}
                    onClose={setIsLanguageModalOpen}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  ) : null
}

export default MobileMegaNav
